import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyArray, getClassTimesOfSchedule } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { CreateEditClassTime } from '@/routes/_layout/orgs/schools/-components/create-edit-class-time';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { distinct, FrequentTimeZones } from '@airreading/common';
import { adjustMinimalClassTimeAcrossTZ, ClassTimeType, ClassTimeWithStudents } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Edit } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DeleteClassTime } from './delete-class-time';
import { CustomTableDialog } from '@/components/ui/custom-table-dialog';
import { ClassTimesEligibleCoachesTable } from './class-times-eligible-coaches-table';

interface ClassTimesTableProps {
  scheduleId: string;
  classTimeTypes?: ClassTimeType[];
  onEditClassTime?: (classTime: ClassTimeWithStudents) => void;
  onDeleteClassTime?: (classTime: ClassTimeWithStudents) => void;
  schoolTimeZone: string;
}

type OpenDialogType = {
  rowId: string;
  type: 'available' | 'conflicts';
};

const columnHelper = createColumnHelper<ClassTimeWithStudents>();

const renderCoachesCell = (
  coaches: ClassTimeWithStudents['availableCoaches'],
  rowId: string,
  dialogType: 'available' | 'conflicts',
  openDialog: OpenDialogType | null,
  setOpenDialog: (openDialog: OpenDialogType | null) => void,
) => {
  const isClickable = coaches && coaches.length > 0;
  const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === dialogType;
  const anchor = (
    <div
      className="cursor-pointer hover:underline"
      onClick={() => (isClickable ? setOpenDialog({ rowId, type: dialogType }) : undefined)}
    >
      {coaches?.length}
    </div>
  );

  return (
    <CustomTableDialog
      open={isOpenDialog}
      setOpen={(open) => setOpenDialog(open ? { rowId, type: dialogType } : null)}
      anchor={anchor}
    >
      <ClassTimesEligibleCoachesTable eligibleCoaches={coaches || []} />
    </CustomTableDialog>
  );
};

const columns = (
  schoolTimeZone: string,
  openDialog: OpenDialogType | null,
  setOpenDialog: (openDialog: OpenDialogType | null) => void,
) => [
  columnHelper.accessor((row) => row, {
    id: 'class-time',
    header: 'Class time (CT)',
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <Badge variant="outline">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor('students', {
    header: 'Number of students',
    cell: ({ getValue }) => {
      const total = getValue()
        .flatMap(({ students }) => students)
        .filter(distinct((student) => student.id)).length;
      return total;
    },
  }),
  columnHelper.accessor('availableCoaches', {
    header: 'Total available teachers',
    id: 'total-available-teachers',
    cell: ({ getValue, row }) => {
      const availableCoaches = getValue();
      return renderCoachesCell(availableCoaches, row.id, 'available', openDialog, setOpenDialog);
    },
  }),
  columnHelper.accessor('availableCoaches', {
    header: 'Conflicts teachers',
    id: 'conflicts-teachers',
    cell: ({ getValue, row }) => {
      const availableCoaches = getValue();
      // Only show coaches that have one time class conflicts
      const conflictsCoaches = availableCoaches?.filter((coach) => coach.conflictType === ClassTimeType.one_time);
      return renderCoachesCell(conflictsCoaches, row.id, 'conflicts', openDialog, setOpenDialog);
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <div className="flex gap-2 items-center">
          <CreateEditClassTime
            from={{
              fromClassTime: classTime,
              mode: 'edit',
            }}
            trigger={
              <Button size="sm">
                <Edit className="h-4 w-4" />
              </Button>
            }
            schoolTimeZone={schoolTimeZone}
            modalTitle={`Edit class time`}
          />
          <DeleteClassTime classTime={classTime} />
        </div>
      );
    },
  }),
];

export function ClassTimesTable({ scheduleId, classTimeTypes, schoolTimeZone }: ClassTimesTableProps) {
  const { data: classTimes, isPending, refetch, error } = useQuery(getClassTimesOfSchedule(scheduleId));
  useErrorToast(error, 'Error loading class times');

  const filteredClassTimes = useMemo(
    () => (classTimeTypes ? classTimes?.filter((classTime) => classTimeTypes.includes(classTime.type)) : classTimes),
    [classTimes, classTimeTypes],
  );

  const [openDialog, setOpenDialog] = useState<OpenDialogType | null>(null);
  const classTimesTableColumns = useMemo(
    () => columns(schoolTimeZone, openDialog, setOpenDialog),
    [schoolTimeZone, openDialog],
  );

  const table = useReactTable({
    data: filteredClassTimes || EmptyArray,
    columns: classTimesTableColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <EntityTable
      table={table}
      isLoading={isPending}
      emptyState={'No class times found for this schedule'}
      errorState={<TableErrorState refetchAction={refetch} />}
      hasError={!!error}
      emptyStateClassName="translate-y-0"
    />
  );
}
